import { useEntries } from "@app/hooks/useEntries";
import { useProjects } from "@app/hooks/useProject";
import { useTiming } from "@app/hooks/useTiming";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { hoursInDay } from "../api/CapsuleProjects";
import type { EntryForHistory } from "../interfaces/CapsuleEntry";
import type { CapsuleOrganisation } from "../interfaces/CapsuleOrganisation";
import type { ProjectSummary } from "../interfaces/ProjectSummary";
import type { UserDetails } from "../interfaces/UserDetails";
import { ShowClosedFilter } from "./ClosedFilter";
import { OrganisationSelect } from "./OrganisationSelect";
import { ProfileMenu } from "./ProfileMenu";
import { ProjectCard } from "./ProjectCard";
import { RecentsFilter } from "./RecentsFilter";
import { SaveTiming, ToggleTimer } from "./Timing";
import { WithQuery } from "./WithQuery";

export const ProjectTable = (props: { user: UserDetails }) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const project = useMemo(() => searchParams.get("project"), [searchParams]);

	const filter = searchParams.get("organisation");

	const sortField = searchParams.get("sort") || "org_name";

	const sortAscending =
		searchParams.get("order") == null
			? true
			: searchParams.get("order") === "asc";

	const showClosed = searchParams.get("c") === "true";

	const showRecents = searchParams.get("r") === "true";

	const [recentFilter, setRecentFilter] = useState<string[]>([]);

	const projectListQuery = useProjects();

	const getEntriesByDate = useEntries();

	const timing = useTiming();

	useEffect(() => {
		if (
			project == null &&
			timing.data != null &&
			timing.data.start_time != null &&
			timing.data.active_project != null
		) {
			console.log("checking");
			const activeProject = timing.data.projects[timing.data.active_project];
			if (activeProject != null) {
				SaveTiming(
					ToggleTimer(
						timing.data.active_project,
						activeProject.link_id,
						timing.data,
					),
				);
				alert(
					"Note: the timer for " +
						timing.data.active_project +
						" was left running and has been paused.",
				);
			}
		}
	}, [project, timing.data]);

	const recents = useMemo(() => {
		if (getEntriesByDate.data == null) {
			return;
		}
		const res = getEntriesByDate.data;
		const s: { [creatorID: string]: EntryForHistory[] } = {};
		res.forEach((r) => {
			if (s[r.CreatorName] == null) {
				s[r.CreatorName] = [r];
			} else {
				s[r.CreatorName].push(r);
			}
		});
		return s;
	}, [getEntriesByDate.data]);

	const handleShowClosedToggle = useCallback(() => {
		if (showClosed) {
			setSearchParams((prev: URLSearchParams) => {
				if (prev.has("c")) {
					prev.delete("c");
				}
				prev.append("c", "false");
				return prev;
			});
		} else {
			setSearchParams((prev: URLSearchParams) => {
				if (prev.has("c")) {
					prev.delete("c");
				}
				prev.append("c", "true");
				return prev;
			});
		}
	}, [setSearchParams, showClosed]);

	const handleOrgFilter = useCallback(
		(org: string) => {
			if (org === "") {
				setSearchParams({});
			} else {
				setSearchParams({ organisation: org });
			}
			setRecentFilter([]);
		},
		[setSearchParams],
	);

	const handleShowRecentsToggle = useCallback(() => {
		if (showRecents) {
			setSearchParams((prev) => {
				if (prev.has("r")) {
					prev.delete("r");
				}
				prev.append("r", "false");
				return prev;
			});
			setRecentFilter([]);
		} else {
			setSearchParams((prev) => {
				if (prev.has("r")) {
					prev.delete("r");
				}
				prev.append("r", "true");
				return prev;
			});
			if (recents != null) {
				const organisations = (recents[props.user.name] || []).map(
					(o) => o.PartyName,
				);
				setRecentFilter(organisations);
			}
		}
	}, [setSearchParams, showRecents, recents, props.user.name]);

	const Headers = useMemo(() => {
		return [
			{
				label: "Organisation",
				field: "org_name",
			},
			{
				label: "Project ID",
				field: "project_id",
			},
			{
				label: "Description",
				field: "project_reference",
			},
			{
				label: "Days Worked",
				field: "days_worked",
			},
			{
				label: "Days Allocated",
				field: "allocated_days",
			},
			{
				label: "Days Remaining",
				field: "days_remaining",
			},
		];
	}, []);

	if (project != null) {
		return <ProjectCard user={props.user}></ProjectCard>;
	}

	return (
		<WithQuery
			query={projectListQuery}
			showLoading={true}
			render={(data: ProjectSummary[] | null) => {
				if (!data) return null;
				const organisations = (projectListQuery.data || [])
					.filter((f) => {
						if (showClosed) {
							return true;
						} else {
							return f.project_status === "OPEN";
						}
					})
					.reduce((acc: ProjectSummary[], curr: ProjectSummary) => {
						const index = acc.findIndex(
							(p) => p.capsule_id === curr.capsule_id,
						);
						if (index === -1) {
							acc.push(curr);
						}
						return acc;
					}, [])
					.sort((a, b) => (a.org_name > b.org_name ? 1 : -1))
					.map((o) => {
						return {
							id: o.capsule_id,
							type: "organisation",
							name: o.org_name,
						} as CapsuleOrganisation;
					});

				let projectList: ProjectSummary[];

				if (!showClosed) {
					projectList = data.filter((f) => f.project_status === "OPEN");
				} else {
					projectList = data;
				}

				if (recentFilter != null && recentFilter.length !== 0) {
					projectList = projectList.filter((f) =>
						recentFilter.includes(f.org_name),
					);
				}

				if (filter != null && filter !== "") {
					projectList = projectList.filter((f) => f.org_name === filter);
				}

				projectList.sort((a: ProjectSummary, b: ProjectSummary) => {
					switch (sortField) {
						case "org_name":
							return `${a.org_name}`.localeCompare(b.org_name);
						case "project_id":
							return `${a.project_id}`.localeCompare(b.project_id);
						case "project_reference":
							return `${a.project_reference}`.localeCompare(
								b.project_reference,
							);
						case "days_worked":
							return b.minutes_worked - a.minutes_worked;
						case "allocated_days":
							return b.allocated_days - a.allocated_days;
						case "days_remaining":
							return b.minutes_remaining - a.minutes_remaining;
						default:
							return `${a.org_name}`.localeCompare(b.org_name);
					}
				});

				if (!sortAscending) {
					projectList.reverse();
				}

				let csvData =
					'"Organisation","Project ID","Description","Days Worked","Days Allocated","Days Remaining"\n';
				(projectList || [])
					.filter(
						(f) =>
							!f.project_id.endsWith("ISP") &&
							!f.project_id.startsWith("CTINT"),
					)
					.sort((a, b) => a.minutes_remaining - b.minutes_remaining)
					.forEach((e) => {
						const worked: string = (e.minutes_worked / 60 / hoursInDay).toFixed(
							2,
						);
						const remaining: string = (
							e.minutes_remaining /
							60 /
							hoursInDay
						).toFixed(2);
						csvData +=
							'"' +
							e.org_name +
							'","' +
							e.project_id +
							'","' +
							e.project_reference +
							'","' +
							worked +
							'","' +
							e.allocated_days +
							'","' +
							remaining +
							'"\n';
					});

				return (
					<>
						<div className={"page-header"}>
							<div className={"flex-row"}>
								<img
									className={"logo"}
									src={"/chartis_logo.png"}
									alt={"logo"}
								/>
								<h2>Project Summaries</h2>
							</div>
							<div className="flex flex-row center">
								<ShowClosedFilter
									showClosed={showClosed}
									setShowClosed={handleShowClosedToggle}
								/>
								<RecentsFilter
									showRecents={showRecents}
									handleShowRecentsToggle={handleShowRecentsToggle}
									disabled={recents == null || recents[props.user.name] == null}
								/>
								<OrganisationSelect
									className={"margin-right padded"}
									filter={filter || ""}
									onChange={handleOrgFilter}
									organisations={organisations}
								/>
							</div>
							<div className={"flex-row margin-right center"}>
								<div className={"flex-wrap center"}>Hi {props.user.name}</div>
								<ProfileMenu
									user={props.user as UserDetails}
									csvData={csvData}
								/>
							</div>
						</div>
						<div className={"project_list-header"}>
							{Headers.map((h) => {
								return (
									<span key={`sp-${h.field}`}>
										<button
											type={"button"}
											key={`hd-${h.field}`}
											className={"project_list-header-item"}
											onClick={() =>
												setSearchParams((prev) => {
													const newParams = new URLSearchParams(prev);
													if (prev.get("sort") !== h.field) {
														newParams.set("sort", h.field);
														newParams.set("order", "asc");
													} else if (prev.get("order") === "desc") {
														newParams.set("order", "asc");
													} else {
														newParams.set("order", "desc");
													}
													return newParams;
												})
											}
										>
											{h.label}
										</button>
									</span>
								);
							})}
						</div>
						{projectList.length === 0 ? (
							filter == null || filter === "" ? (
								<div className={"project_list"}>
									<i>No Projects found.</i>
								</div>
							) : (
								<div className={"project_list-no_row"}>
									<i>
										No Projects found for <b>{filter}</b>
									</i>
								</div>
							)
						) : null}
						<div className="project_list">
							{projectList.map((p) => {
								let rowClass = "project_list-row";
								if (p.project_status !== "OPEN") {
									rowClass = `${rowClass} project_closed`;
								}
								return (
									<button
										key={p.id}
										type={"button"}
										className={rowClass}
										onClick={() => {
											setSearchParams({ project: p.id.toFixed(0) });
										}}
									>
										<span key={`${p.id}_org_name`}>{p.org_name}</span>
										<span key={`${p.id}_project_id`}>{p.project_id}</span>
										<span key={`${p.id}_reference`}>{p.project_reference}</span>
										<div>
											{p.project_id.endsWith("ISP") ? (
												"-"
											) : (
												<span key={`${p.id}_worked`}>
													{(p.minutes_worked / 60 / hoursInDay).toFixed(2)}
												</span>
											)}
										</div>
										<span key={`${p.id}_allocated`}>{p.allocated_days}</span>
										<div key={`${p.id}_remaining`}>
											{p.project_id.endsWith("ISP") ? (
												"-"
											) : p.minutes_remaining === 0 &&
												p.minutes_worked === 0 ? (
												<span>{p.allocated_days}</span>
											) : (
												<>
													<span>
														{(p.minutes_remaining / 60 / hoursInDay).toFixed(2)}
													</span>
													<span className={"low-minutes"}>
														{p.minutes_remaining < 45 ? " !" : ""}
													</span>
												</>
											)}
										</div>
									</button>
								);
							})}
						</div>
					</>
				);
			}}
		/>
	);
};
